import React, { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'

import {
  Box,
  Divider,
  List,
  Collapse,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography
} from '@mui/material'
import { useTheme } from '@mui/material/styles'

import HomeIcon from '@mui/icons-material/Home'
import ScanQRIcon from '@mui/icons-material/Videocam';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer'
import TodayIcon from '@mui/icons-material/Today'
import NearMeIcon from '@mui/icons-material/NearMe'
import AppsIcon from '@mui/icons-material/Apps'
import AccountCircle from '@mui/icons-material/AccountCircle'
import ChatIcon from '@mui/icons-material/Chat'
import MenuBookIcon from '@mui/icons-material/MenuBook'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import FavoriteIcon from '@mui/icons-material/Favorite'
import LocalBarIcon from '@mui/icons-material/LocalBar'
import GroupsIcon from '@mui/icons-material/Groups'
import PolicyIcon from '@mui/icons-material/Policy'
import GavelIcon from '@mui/icons-material/Gavel'
import StorefrontIcon from '@mui/icons-material/Storefront'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

import CurrentUserAvatar from './CurrentUserAvatar'
import {
  contentColumnAnchorSx,
  contentColumnBackdropSx
} from './contentColumn'
import UIStateContainer from '../Containers/UIStateContainer'
import UserAuthContainer from '../Containers/UserAuthContainer'
import useNavigation from '../Utils/navigationUtils'
import ROUTES from '../../routesConfig'
import anearApi from '../Api/AnearApi'

const LinkStyling = {
  textDecoration: 'none'
}

const MENU_DRAWER_WIDTH_PX = 250

const useMenuReveal = (open, exitMs) => {
  const [mounted, setMounted] = useState(false)
  const [revealed, setRevealed] = useState(false)
  const [animate, setAnimate] = useState(false)
  const panelRef = React.useRef(null)

  useEffect(() => {
    if (open) {
      setMounted(true)
      return undefined
    }

    setRevealed(false)
    const timer = window.setTimeout(() => {
      setMounted(false)
      setAnimate(false)
    }, exitMs)
    return () => window.clearTimeout(timer)
  }, [open, exitMs])

  // Paint the closed position before enabling CSS transition (open often skipped with rAF in useEffect).
  React.useLayoutEffect(() => {
    if (!open || !mounted) return undefined

    setAnimate(false)
    setRevealed(false)

    const panel = panelRef.current
    if (panel) {
      panel.style.transition = 'none'
      panel.style.transform = 'translateX(-100%)'
      void panel.offsetHeight
      panel.style.removeProperty('transition')
      panel.style.removeProperty('transform')
    }

    let raf2 = 0
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        setAnimate(true)
        setRevealed(true)
      })
    })

    return () => {
      cancelAnimationFrame(raf1)
      if (raf2) cancelAnimationFrame(raf2)
    }
  }, [open, mounted])

  return { mounted, revealed, animate, panelRef }
}

/**
 * Homegrown overlay: fixed clip frame on the content column edge, panel reveals
 * via translateX inside overflow:hidden. No MUI Drawer/Slide/viewport math.
 */
const BurgerMenuOverlay = ({ open, onClose, children }) => {
  const theme = useTheme()
  const exitMs = theme.transitions.duration.leavingScreen
  const { mounted, revealed, animate, panelRef } = useMenuReveal(open, exitMs)

  useEffect(() => {
    if (!mounted) return undefined

    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [mounted, onClose])

  useEffect(() => {
    if (!mounted) return undefined

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [mounted])

  if (!mounted) return null

  const panelTransition = theme.transitions.create('transform', {
    duration: revealed
      ? theme.transitions.duration.enteringScreen
      : theme.transitions.duration.leavingScreen,
    easing: revealed
      ? theme.transitions.easing.easeOut
      : theme.transitions.easing.sharp
  })

  const backdropTransition = theme.transitions.create('opacity', {
    duration: revealed
      ? theme.transitions.duration.enteringScreen
      : theme.transitions.duration.leavingScreen
  })

  return createPortal(
    <>
      <Box
        aria-hidden
        onClick={onClose}
        sx={themeArg => ({
          ...contentColumnBackdropSx(themeArg),
          zIndex: themeArg.zIndex.drawer - 1,
          bgcolor: 'rgba(0, 0, 0, 0.5)',
          opacity: revealed ? 1 : 0,
          transition: backdropTransition,
          pointerEvents: revealed ? 'auto' : 'none'
        })}
      />
      <Box
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
        sx={themeArg => ({
          position: 'fixed',
          top: 0,
          bottom: 0,
          width: MENU_DRAWER_WIDTH_PX,
          maxWidth: '85vw',
          overflow: 'hidden',
          zIndex: themeArg.zIndex.drawer,
          pointerEvents: 'none',
          ...contentColumnAnchorSx(themeArg)
        })}
      >
        <Box
          ref={panelRef}
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: '#1A1F2E',
            color: '#E6E9EF',
            boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
            transform: revealed ? 'translateX(0)' : 'translateX(-100%)',
            transition: animate ? panelTransition : 'none',
            pointerEvents: 'auto'
          }}
        >
          {children}
        </Box>
      </Box>
    </>,
    document.body
  )
}

const extractAppSlug = (app) => {
  const attrs = app?.app?.attributes || {}
  return attrs.slug || attrs['slug'] || app?.slug || app?.app?.slug || null
}

const BurgerMenu = () => {
  const [isLearnOpen, setIsLearnOpen] = useState(false)
  const [perimeterChatApp, setPerimeterChatApp] = useState(null)
  const [appsFetched, setAppsFetched] = useState(false)

  const uiContainer = UIStateContainer.useContainer()
  const userAuthContainer = UserAuthContainer.useContainer()
  const { navigateToProfile, resolveHomePath } = useNavigation()

  // Fetch apps to find Perimeter Chat app (only once, not on every menu open)
  // Since BurgerMenu stays mounted, this only runs once on app load
  useEffect(() => {
    // Skip if already fetched to avoid redundant API calls
    if (appsFetched) return

    let cancelled = false

    const fetchApps = async () => {
      try {
        const apps = await anearApi.globalPlayableApps()
        if (!cancelled) {
          setAppsFetched(true)
          // Find Perimeter Chat app by short-name
          const chatApp = apps.find(app =>
            app.app.attributes['short-name'] === 'Perimeter Chat'
          )
          if (chatApp) {
            const appAttrs = {
              zoneId: chatApp.zone.id,
              appId: chatApp.app.id,
              slug: extractAppSlug(chatApp),
              shortName: chatApp.app.attributes['short-name'],
              longName: chatApp.app.attributes['long-name'],
              iconUrl: chatApp.app.attributes['icon-url'],
              description: chatApp.app.attributes['description'],
              requiresGeoLocation: chatApp.eventRequiresGeoLocation()
            }
            setPerimeterChatApp(appAttrs)
          }
        }
      } catch (err) {
        console.error('Error fetching apps for Chat menu:', err)
        if (!cancelled) setAppsFetched(true) // Mark as fetched even on error to prevent retries
      }
    }

    fetchApps()
    return () => { cancelled = true }
  }, [appsFetched])

  const menuLink = (ComponentIcon, label, route, { indent = 0, state = null } = {}) => (
    <Box
      component={Link}
      to={route}
      state={state}
      key={label}
      sx={LinkStyling}
      onClick={() => {
        uiContainer.closeBurgerMenu()
      }}
    >
      <ListItemButton
        sx={{
          color: '#E6E9EF',
          pl: indent ? 4 : undefined,
          '&:hover': {
            bgcolor: 'rgba(255,255,255,0.08)'
          }
        }}
      >
        <ListItemIcon sx={{ color: '#94A3B8' }}>
          <ComponentIcon />
        </ListItemIcon>
        <ListItemText
          primary={label}
          primaryTypographyProps={{
            sx: { color: '#CBD5E1', fontWeight: 500 }
          }}
        />
      </ListItemButton>
    </Box>
  )

  const accountInfo = name => (
    <>
      <ListItemIcon>
        <CurrentUserAvatar size={'mediumAvatar'} />
      </ListItemIcon>
      <ListItemText
        primary={<Typography sx={{ pl: 1, color: '#E6E9EF', fontWeight: 600 }}>{name}</Typography>}
      />
    </>
  )

  const unauthdInfo = () => (
    <>
      <ListItemIcon sx={{ minWidth: 0, mr: 2 }}>
        <AccountCircle
          sx={{
            color: '#E5E7EB',
            height: 40,
            width: 40
          }}
        />
      </ListItemIcon>
    </>
  )

  const userProfileItem = (userIsAuthed, userName) => {
    const onProfileClick = () => {
      uiContainer.closeBurgerMenu()
      navigateToProfile()
    }

    return (
      <ListItemButton
        onClick={onProfileClick}
        sx={{
          color: '#E6E9EF',
          cursor: 'pointer',
          '&:hover': {
            bgcolor: 'rgba(255,255,255,0.08)'
          }
        }}
      >
        {userIsAuthed ? accountInfo(userName) : unauthdInfo()}
      </ListItemButton>
    )
  }

  // Build menu rows - Chat link is dynamic based on Perimeter Chat app
  const getMenuSections = () => {
    const chatRoute = perimeterChatApp
      ? ROUTES.CREATE_EVENT(perimeterChatApp.slug || perimeterChatApp.zoneId)
      : ROUTES.CHAT
    const chatState = perimeterChatApp
      ? { playableApp: perimeterChatApp }
      : null

    const primaryRows = [
      [HomeIcon,   "Home",          resolveHomePath(), { indent: 0 }],
      [ScanQRIcon, "Scan QR",       ROUTES.SCAN, { indent: 0 }],
      [AppsIcon,   "Games",         ROUTES.APPS, { indent: 0 }],
      [TodayIcon,  "My Events",     ROUTES.EVENTS, { indent: 0 }],
      [NearMeIcon, "What's Nearby", ROUTES.NEARBY, { indent: 0 }],
      [ChatIcon,   "Chat",          chatRoute, { indent: 0, state: chatState }],
    ]

    const privilegedRows = []

    if (userAuthContainer.isMerchant()) {
      privilegedRows.push([StorefrontIcon, "Merchant Dashboard", ROUTES.MERCHANT_DASHBOARD, { indent: 0 }])
    }

    return { primaryRows, privilegedRows }
  }

  const { primaryRows, privilegedRows } = getMenuSections()

  const learnRows = [
    [QuestionAnswerIcon, "About",        ROUTES.ABOUT],
    [HelpOutlineIcon,    "How it works", ROUTES.HOW_IT_WORKS],
    [FavoriteIcon,       "Weddings",     ROUTES.WEDDINGS],
    [LocalBarIcon,       "Bars & Venues",ROUTES.VENUES],
    [GroupsIcon,         "Meetups",      ROUTES.MEETUPS],
  ]

  const menuItems = (
    <Box sx={{ width: 250, minHeight: 'calc(100svh - 73px)', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ flexGrow: 1 }}>
        <List>
          {primaryRows.map(menuRow => {
            return menuLink(...menuRow)
          })}
        </List>
        {privilegedRows.length > 0 && (
          <>
            <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
            <List>
              {privilegedRows.map(menuRow => {
                return menuLink(...menuRow)
              })}
            </List>
          </>
        )}
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
        <List>
          <ListItemButton
            onClick={() => setIsLearnOpen(prev => !prev)}
            sx={{
              pt: 1.25,
              pb: 0.25,
              color: '#E6E9EF',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.08)'
              }
            }}
          >
            <ListItemIcon sx={{ color: '#94A3B8' }}>
              <MenuBookIcon />
            </ListItemIcon>
            <ListItemText
              primary="Learn"
              primaryTypographyProps={{
                sx: { color: '#CBD5E1', fontWeight: 700, letterSpacing: 0.2 }
              }}
            />
            {isLearnOpen ? (
              <ExpandLessIcon sx={{ color: '#94A3B8' }} />
            ) : (
              <ExpandMoreIcon sx={{ color: '#94A3B8' }} />
            )}
          </ListItemButton>
          <Collapse in={isLearnOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {learnRows.map(menuRow => {
                return menuLink(...menuRow, { indent: 1 })
              })}
            </List>
          </Collapse>
        </List>
      </Box>

      <Box sx={{ mt: 'auto' }}>
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
        <List sx={{ pt: 0.5, pb: 1 }}>
          {menuLink(PolicyIcon, 'Privacy Policy', ROUTES.PRIVACY)}
          {menuLink(GavelIcon, 'Terms of Service', ROUTES.TERMS)}
        </List>
      </Box>
    </Box>
  )

  const userIsAuthed = userAuthContainer.isAuthed()

  return (
    <BurgerMenuOverlay
      open={uiContainer.isBurgerMenuOpen}
      onClose={uiContainer.closeBurgerMenu}
    >
      <Box tabIndex={0} role="button">
        { userProfileItem(userIsAuthed, userAuthContainer.name()) }
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
        {menuItems}
      </Box>
    </BurgerMenuOverlay>
  )
}

export default BurgerMenu
